// Engagement_Dashboard_Old.utils.ts
import Highcharts from "highcharts/highstock";

/**
 * Shared types
 */
export type SelectOption = { label: string; value: string };

export type DashboardFilters = {
  businessUnits?: string[]; // "Line of Service" UI, maps to BusinessUnit in backend
  offices?: string[];
  partners?: string[];
  fiscalYears?: string[]; // if backend supports FY, else keep for UI only
};

export type DashboardFiltersPayload = {
  BusinessUnit?: string[]; // backend expects list of strings OR omit
  Office?: string[];
  PrimaryPartner?: string[];
  FiscalYear?: string[]; // optional, only include if your API supports it
};

export type ApiContext<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

export type ApprovalStatusRow = {
  ApprovalStatus: string;
  TotalCount: number;
};

export type SignatureStatusRow = {
  SignatureStatus: string;
  TotalCount: number;
};

export type OfficeStatusRow = {
  Office: string;
  ApprovalStatus: string;
  TotalCount: number;
};

export type PartnerStatusRow = {
  PrimaryPartner: string;
  ApprovalStatus: string;
  TotalCount: number;
};

export type DropdownStoreState = {
  purposeOptions?: Array<{ label: string; value: string; code?: string }>; // Line of Service
  officeLocationOptions?: Array<{ label: string; value: string; id?: string }>;
  primaryPartnerOptions?: Array<{ label: string; value: string; id?: string; location?: string }>;
};

export type ThemeColors = {
  primary: string;
  tertiary: string;
};

/**
 * Constants
 */
export const ALL_OPTION: SelectOption = { label: "All", value: "All" };

export const STATUS_COLOR_MAP: Record<string, string> = {
  Approved: "#388E3C",
  "Out For Signature": "#003f5c",
  Draft: "#FFD54F",
  "Partially Approved": "#FF7F00",
  "Pending Approval": "#1976D2",
  Rejected: "#D32F2F",
  "Delivery Failed": "#D32F2F",
  "E-Signed": "#388E3C",
};

export const FIXED_APPROVAL_STATUS_ORDER = [
  "Draft",
  "Pending Approval",
  "Partially Approved",
  "Rejected",
  "Approved",
] as const;

/**
 * Helpers
 */
export const buildFiltersPayload = (filters: DashboardFilters): DashboardFiltersPayload => {
  const payload: DashboardFiltersPayload = {};

  if (filters.businessUnits?.length) payload.BusinessUnit = filters.businessUnits;
  if (filters.offices?.length) payload.Office = filters.offices;
  if (filters.partners?.length) payload.PrimaryPartner = filters.partners;
  if (filters.fiscalYears?.length) payload.FiscalYear = filters.fiscalYears;

  return payload;
};

export const selectOptionsFromStrings = (values: string[]): SelectOption[] =>
  values.map((v) => ({ label: v, value: v }));

export const optionOrAll = (value?: string | null): SelectOption =>
  value ? { label: value, value } : ALL_OPTION;

export const pickDefaultBusinessUnit = (
  store: DropdownStoreState | null | undefined
): SelectOption => {
  const first = store?.purposeOptions?.[0]?.value;
  return first ? { label: store!.purposeOptions![0].label, value: first } : ALL_OPTION;
};

export const pickDefaultOffice = (
  store: DropdownStoreState | null | undefined,
  myOffice?: string | null
): SelectOption => {
  const officeValue =
    myOffice ||
    store?.officeLocationOptions?.find((o) => o.value)?.value ||
    store?.officeLocationOptions?.[0]?.value;

  if (!officeValue) return ALL_OPTION;

  const label =
    store?.officeLocationOptions?.find((o) => o.value === officeValue)?.label || officeValue;

  return { label, value: officeValue };
};

export const pickDefaultPartner = (
  store: DropdownStoreState | null | undefined
): SelectOption => {
  const first = store?.primaryPartnerOptions?.[0]?.value;
  const label = store?.primaryPartnerOptions?.[0]?.label;
  return first ? { label: label || first, value: first } : ALL_OPTION;
};

/**
 * If you need "current Tax year" default:
 * - Define the FY naming convention you use (FY2026 etc)
 * - This assumes US tax year aligned to calendar year.
 * Change logic if you use fiscal year Jul-Jun.
 */
export const getCurrentTaxYearValue = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  return `FY${year}`;
};

export const safeNumber = (n: unknown): number => (typeof n === "number" && Number.isFinite(n) ? n : 0);

/**
 * Highcharts - shared defaults
 */
export const chartDefaults: Highcharts.Options = {
  lang: { noData: "No data available" },
  noData: {
    style: {
      fontWeight: "bold",
      fontSize: "16px",
      color: "#6E6E6E",
    },
  },
};

type ColumnSeries = Highcharts.SeriesColumnOptions;
type BarSeries = Highcharts.SeriesBarOptions;
type PieSeries = Highcharts.SeriesPieOptions;

/**
 * Totals / KPIs
 */
export const getTotalCountFromRows = (rows: Array<{ TotalCount: number }>): number =>
  rows.reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

/**
 * Chart builders
 */

/**
 * Approval Status Distribution (column)
 * backend rows: [{ ApprovalStatus, TotalCount }]
 */
export const buildApprovalStatusBarOptions = (rows: ApprovalStatusRow[]): Highcharts.Options => {
  const countByStatus = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.ApprovalStatus] = (acc[r.ApprovalStatus] || 0) + safeNumber(r.TotalCount);
    return acc;
  }, {});

  const series: ColumnSeries[] = [
    {
      type: "column",
      name: "Count",
      data: [...FIXED_APPROVAL_STATUS_ORDER].map((status) => ({
        name: status,
        y: countByStatus[status] ?? 0,
        color: STATUS_COLOR_MAP[status] || undefined,
      })),
    },
  ];

  return {
    ...chartDefaults,
    chart: { type: "column" },
    title: { text: "EL Approval Status Distribution" },
    xAxis: {
      categories: [...FIXED_APPROVAL_STATUS_ORDER],
      title: { text: "Status" },
      labels: { style: { fontSize: "12px" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      allowDecimals: false,
    },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y}</b> ELs" },
    plotOptions: { column: { pointWidth: 30 } },
    series,
  } satisfies Highcharts.Options;
};

/**
 * Signature Status Distribution (pie)
 * backend rows: [{ SignatureStatus, TotalCount }]
 */
export const buildSignatureStatusPieOptions = (
  rows: SignatureStatusRow[]
): { options: Highcharts.Options; total: number } => {
  const total = getTotalCountFromRows(rows);

  const series: PieSeries[] = [
    {
      type: "pie",
      name: "Count",
      data: rows.map((r) => ({
        name: r.SignatureStatus,
        y: safeNumber(r.TotalCount),
        color: STATUS_COLOR_MAP[r.SignatureStatus] || undefined,
      })),
    },
  ];

  return {
    total,
    options: {
      ...chartDefaults,
      chart: { type: "pie" },
      title: { text: "EL Signature Status Distribution" },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: { enabled: true, format: "<b>{point.name}</b>: {point.y}" },
        },
      },
      legend: {
        enabled: true,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },
      series,
    } satisfies Highcharts.Options,
  };
};

/**
 * Partner x Status stacked bar (bar)
 * backend rows: [{ PrimaryPartner, ApprovalStatus, TotalCount }]
 */
export const buildPartnerStackedBarOptions = (
  rows: PartnerStatusRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const partners = Array.from(new Set(rows.map((r) => r.PrimaryPartner))).filter(Boolean);
  // sort by totals desc
  const totals = partners
    .map((p) => ({
      p,
      total: rows
        .filter((r) => r.PrimaryPartner === p)
        .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.p);

  const getVal = (partner: string, status: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const series: BarSeries[] = [...FIXED_APPROVAL_STATUS_ORDER].map((status) => ({
    type: "bar",
    name: status,
    data: totals.map((partner) => getVal(partner, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: { type: "bar" },
    title: { text: "EL Distribution by Primary Partner and Status" },
    xAxis: {
      categories: totals,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: 4,
      scrollbar: {
        enabled: true,
        showFull: false,
        barBorderRadius: 6,
        barBorderWidth: 0,
        trackBorderRadius: 6,
        trackBorderWidth: 0,
        buttonBorderWidth: 0,
        mouseWheelEnabled: true,
        // Keep styling minimal to avoid TS friction; optionally add colors if your typings allow it:
        // barBackgroundColor: theme?.tertiary,
        // trackBackgroundColor: theme?.primary,
      } as any,
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      stackLabels: { enabled: true },
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 30,
      },
    },
    series,
  } satisfies Highcharts.Options;
};

/**
 * Office x Status stacked column (column)
 * backend rows: [{ Office, ApprovalStatus, TotalCount }]
 */
export const buildOfficeStackedColumnOptions = (rows: OfficeStatusRow[]): Highcharts.Options => {
  const offices = Array.from(new Set(rows.map((r) => r.Office))).filter(Boolean);
  // sort by totals desc
  const totals = offices
    .map((o) => ({
      o,
      total: rows.filter((r) => r.Office === o).reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.o);

  const getVal = (office: string, status: string) =>
    rows
      .filter((r) => r.Office === office && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const series: ColumnSeries[] = [...FIXED_APPROVAL_STATUS_ORDER].map((status) => ({
    type: "column",
    name: status,
    data: totals.map((office) => getVal(office, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: { type: "column" },
    title: { text: "Total ELs by Office" },
    xAxis: {
      categories: totals,
      crosshair: true,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      stackLabels: { enabled: true },
      gridLineWidth: 0,
      lineWidth: 0,
      reversedStacks: false,
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 30,
      },
    },
    series,
  } satisfies Highcharts.Options;
};
